import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { useLanguage } from "@/contexts/LanguageContext";
import FounderAudioButton from "@/components/FounderAudioButton";
import { getFounderAudioSrc } from "@/lib/foundersAudio";

// -----------------------------------------------------------------------------
//  FoundersSection — homepage editorial block dedicated to Grup Xaluca
//  and Xaluca Tours' two founders.
//
//  Design intent
//  ─────────────
//  • Warm, human, nostalgic aesthetic — two polaroids (one per founder)
//    paired with a hand-written note.
//  • Polaroids are lightly tilted in opposite directions to add authenticity
//    and break the "grid" feeling. Each carries the founder's name and
//    origin as a caption on the white margin.
//  • Notes sit on a soft cream / paper background with a faint diagonal
//    line pattern that reads as "letter paper" without competing with the
//    typography.
//  • Layout: polaroid + note side-by-side on desktop (md:), stacked
//    (polaroid → note) on tablet and mobile. Generous vertical spacing
//    between the two founders so each story breathes.
//
//  Images are `data-cms-role="card"` + scoped, so the operator can swap
//  the placeholder portraits for real family photographs via the admin
//  edit mode without touching the code.
// -----------------------------------------------------------------------------

const FOUNDERS = [
  {
    id: "tayeb-ettaiek",
    name: "Tayeb Ettaiek",
    origin: {
      en: "Arfoud, Morocco",
      fr: "Arfoud, Maroc",
      es: "Arfoud, Marruecos",
    },
    photo:
      "https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/lpqsg154_Tayeb%20Ettaiek.jpg",
    tilt: "-rotate-[2.5deg] md:-rotate-[3deg]",
    letter: {
      en: [
        "From Arfoud, Tayeb Ettaiek carries the local soul — Moroccan hospitality and a deep, lived knowledge of the south. His reading of the land, its people, and their traditions has been essential to shaping experiences that feel authentic, caring, and truly rooted in place.",
        "His legacy lives on in every welcome, every route, and every small detail that turns a journey into a memory.",
      ],
      fr: [
        "Depuis Arfoud, Tayeb Ettaiek incarne l'âme locale, l'hospitalité marocaine et une connaissance profonde du sud du pays. Son regard sur le territoire, ses habitants et leurs traditions a été essentiel pour bâtir des expériences authentiques, soignées et profondément ancrées dans le lieu.",
        "Son héritage vit dans chaque accueil, chaque route et chaque détail qui transforme un voyage en souvenir.",
      ],
      es: [
        "Desde Arfoud, Tayeb Ettaiek representa el alma local, la hospitalidad marroquí y el profundo conocimiento del sur de Marruecos. Su mirada sobre el territorio, sus gentes y sus tradiciones ha sido clave para dar forma a experiencias auténticas, cuidadas y profundamente conectadas con el lugar.",
        "Su legado vive en cada bienvenida, en cada ruta y en cada detalle que convierte un viaje en una experiencia memorable.",
      ],
    },
  },
  {
    id: "lluis-pont",
    name: "Lluís Pont",
    origin: {
      en: "Sabadell, Spain",
      fr: "Sabadell, Espagne",
      es: "Sabadell, España",
    },
    photo:
      "https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/8jj2ff2n_Llui%CC%81s%20Pont.jpg",
    tilt: "rotate-[2deg] md:rotate-[2.5deg]",
    letter: {
      en: [
        "From Sabadell, Lluís Pont championed a way of travelling grounded in trust, closeness, and respect for the destination. His vision helped build the bridge between those who dream of discovering Morocco and those who live it from within, day after day.",
        "A family story — of travel and of people — that keeps inspiring every experience shaped by Grup Xaluca and Xaluca Tours.",
      ],
      fr: [
        "Depuis Sabadell, Lluís Pont a défendu une manière de voyager fondée sur la confiance, la proximité et le respect du territoire. Sa vision a bâti le pont entre celles et ceux qui rêvent de découvrir le Maroc et celles et ceux qui le vivent, chaque jour, de l'intérieur.",
        "Une histoire familiale, voyageuse et profondément humaine qui continue d'inspirer chaque expérience Grup Xaluca et Xaluca Tours.",
      ],
      es: [
        "Desde Sabadell, Lluís Pont impulsó una manera de viajar basada en la confianza, la cercanía y el respeto por el destino. Su visión ayudó a construir el puente entre quienes sueñan con descubrir Marruecos y quienes lo viven desde dentro cada día.",
        "Una historia familiar, viajera y humana que sigue inspirando cada experiencia de Grup Xaluca y Xaluca Tours.",
      ],
    },
  },
];

const COPY = {
  overline: {
    en: "OUR FOUNDERS",
    fr: "NOS FONDATEURS",
    es: "NUESTROS FUNDADORES",
  },
  title: {
    en: "The people who lit the path",
    fr: "Celles et ceux qui ont ouvert la route",
    es: "Las personas que abrieron el camino",
  },
  intro: {
    en: "Two paths. Two homes. One shared belief — that travel is a bridge between people, and that every welcome, every route, every detail should feel like family.",
    fr: "Deux chemins. Deux terres. Une même conviction — que voyager, c'est bâtir des ponts entre les gens, et que chaque accueil, chaque route, chaque détail doit avoir un goût de famille.",
    es: "Dos caminos. Dos tierras. Una misma certeza — que viajar es construir puentes entre personas, y que cada bienvenida, cada ruta, cada detalle debe sentirse como parte de la familia.",
  },
  cta: {
    en: "Meet our team",
    fr: "Rencontrez notre équipe",
    es: "Conoce a nuestro equipo",
  },
};

// ─────────────────────────────────────────────────────────────────────────
//  Polaroid + Letter
// ─────────────────────────────────────────────────────────────────────────
const FounderBlock = ({ founder, lang, reverse }) => {
  const origin = founder.origin[lang] || founder.origin.en;
  const letter = founder.letter[lang] || founder.letter.en;
  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 lg:gap-16 items-center ${
        reverse ? "md:[&>*:first-child]:col-start-7" : ""
      }`}
      data-testid={`founder-${founder.id}`}
    >
      {/* Polaroid — square-ish photo with a caption on the bottom margin.
          Slight tilt gives a hand-placed, on-a-desk feel. The shadow adds
          a subtle physical depth. */}
      <div
        className={`md:col-span-5 ${reverse ? "md:col-start-8" : ""} flex justify-center md:justify-start`}
      >
        <figure
          className={`inline-block bg-white p-3 pb-6 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)] transform ${founder.tilt} transition-transform duration-500 hover:rotate-0 hover:scale-[1.02]`}
          data-testid={`founder-polaroid-${founder.id}`}
        >
          <div className="w-[240px] h-[280px] sm:w-[260px] sm:h-[300px] md:w-[280px] md:h-[320px] overflow-hidden bg-[#F2EBE1]">
            <img
              src={founder.photo}
              alt={founder.name}
              loading="lazy"
              decoding="async"
              data-cms-role="card"
              data-cms-scope={`home.founders.${founder.id}.photo`}
              className="w-full h-full object-cover"
            />
          </div>
          <figcaption className="mt-4 px-1 text-center">
            <div
              className="font-handwritten text-[#2C2621] tracking-tight text-2xl md:text-3xl leading-tight"
              data-testid={`founder-caption-name-${founder.id}`}
            >
              {founder.name}
            </div>
            <div className="mt-1 text-[10px] tracking-[0.28em] uppercase text-[#5C5248]">
              {origin}
            </div>
          </figcaption>
        </figure>
      </div>

      {/* Handwritten letter — cream paper background with a faint horizontal
          line pattern that reads as "notebook paper" without stealing the
          eye from the text. */}
      <article
        className={`md:col-span-6 ${reverse ? "md:col-start-1 md:row-start-1" : "md:col-start-7"}`}
      >
        <div
          className="relative bg-[#F5EDDF] px-6 py-8 md:px-10 md:py-12 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.7)]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(180deg, rgba(44,38,33,0) 0px, rgba(44,38,33,0) 34px, rgba(193,101,66,0.10) 35px)",
          }}
        >
          <h3
            className="font-serif-x text-3xl md:text-4xl leading-tight text-[#2C2621]"
            data-testid={`founder-letter-name-${founder.id}`}
          >
            {founder.name}
          </h3>
          <div className="mt-6 space-y-4 text-base md:text-lg leading-[1.9] text-[#3D342D]">
            {letter.map((p, i) => (
              <p key={`${founder.id}-p-${i}`}>{p}</p>
            ))}
          </div>

          {/* Founder audio player — thank-you voice message. Audio slot
              lives in /lib/foundersAudio.js; while `null`, the button
              renders the disabled "coming soon" pill so the structure
              is visible ahead of the recording. Only one message can
              play at a time (site-wide, coordinated in the button). */}
          <FounderAudioButton
            audioSrc={getFounderAudioSrc(founder.id)}
            founderId={founder.id}
            name={founder.name}
            lang={lang}
          />

          <div
            className="mt-8 md:mt-10 font-signature text-3xl md:text-4xl text-[#C16542]"
            data-testid={`founder-signature-${founder.id}`}
          >
            — {founder.name}
          </div>
        </div>
      </article>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  Section
// ─────────────────────────────────────────────────────────────────────────
const FoundersSection = () => {
  const { lang } = useLanguage();
  return (
    <section
      className="relative py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] overflow-hidden"
      data-testid="section-founders"
      data-cms-scope="home.founders"
    >
      {/* Shared dark-section pattern language — same three layers used on
          the Gastronomy strip so both dark blocks read as one coherent
          graphic identity across the homepage. */}
      <div className="absolute inset-0 berber-bg-cross opacity-40 pointer-events-none" aria-hidden="true" />
      <div className="absolute inset-0 moroccan-bg-sandgrid opacity-90 mix-blend-screen pointer-events-none" aria-hidden="true" />
      <div
        className="hidden lg:block absolute bottom-0 inset-x-0 h-40 moroccan-bg-arches opacity-25 pointer-events-none"
        aria-hidden="true"
      />
      {/* Subtle warm vignette — mimics the glow of a desk lamp over
          hand-placed keepsakes, adds depth without competing with the
          content. Kept on top of the pattern so it still centers focus. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(193,101,66,0.10) 0%, rgba(26,21,19,0) 55%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Editorial header — same 12-col rhythm as sibling sections, with
            typography lifted to cream for strong contrast on the dark
            surface. */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-16 md:mb-24">
          <div className="md:col-span-5">
            <p className="overline text-[#C16542]">
              {COPY.overline[lang] || COPY.overline.en}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#FDFBF7]">
              {COPY.title[lang] || COPY.title.en}
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 self-end text-base md:text-lg text-[#FDFBF7]/70 leading-relaxed italic">
            {COPY.intro[lang] || COPY.intro.en}
          </p>
        </div>

        {/* Two founder blocks — alternate polaroid position to keep the
            page's rhythm dynamic. Generous vertical breathing room between
            the two so each story truly stands on its own. */}
        <div className="space-y-24 md:space-y-32">
          {FOUNDERS.map((f, i) => (
            <FounderBlock
              key={f.id}
              founder={f}
              lang={lang}
              reverse={i % 2 === 1}
            />
          ))}
        </div>

        {/* Bridge to /team — the founders' story naturally opens onto the
            wider Grup Xaluca ecosystem. */}
        <div className="mt-20 md:mt-28 flex justify-center">
          <Link
            to="/team"
            className="inline-flex items-center gap-2 bg-[#FDFBF7] text-[#1A1513] px-6 py-3.5 text-xs tracking-[0.3em] uppercase hover:bg-[#C16542] hover:text-[#FDFBF7] transition-colors"
            data-testid="founders-cta-team"
          >
            {COPY.cta[lang] || COPY.cta.en}
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FoundersSection;
